<!-- ⚠ LIGHT THEME, DELIBERATELY. The preview cards are white because a chart's contrast gate is
     checked against a white surface (`surface: '#FFFFFF'` in draw()). On the old dark chrome the
     cards were the only light thing on screen, so the page and the thing it was previewing disagreed
     about what a chart sits on. Now the page IS the surface. -->
<!-- ⚠ THIS ELEMENT OWNS THE SCROLL. `body` is `overflow: hidden; height: 100%` globally
     (styles.css), so every screen scrolls inside its own container - the project list does the same
     thing with an overflow-y-auto wrapper. `min-h-screen` here grew the page past a clipped body
     and nothing scrolled at all. The sticky header still works, because sticky positions against
     the nearest scrolling ancestor, which is now this. -->
<div class="h-screen overflow-y-auto bg-white text-gray-900">

  <!-- Header -->
  <div class="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-200">
    <div class="max-w-[1600px] mx-auto px-5 py-3 flex items-center gap-4">
      <button id="chart-style-back" (click)="back()"
              class="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        Projects
      </button>
      <div class="flex-1">
        <h1 class="text-lg font-semibold tracking-tight text-gray-900">Chart style</h1>
        <p class="text-xs text-gray-500">Your default look. New projects start from this; each one can
          change its own afterwards.</p>
      </div>
      <span *ngIf="saved" class="text-xs text-emerald-600">Saved</span>
      <span *ngIf="error" class="text-xs text-rose-600">{{ error }}</span>
      <button (click)="reset()"
              class="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors">
        Reset
      </button>
      <button id="chart-style-save" (click)="save()" [disabled]="saving"
              class="px-4 py-1.5 text-sm rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white font-medium transition-colors">
        {{ saving ? 'Saving…' : 'Save as my default' }}
      </button>
    </div>
  </div>

  <div class="max-w-[1600px] mx-auto px-5 py-5 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">

    <!-- ── Knobs ────────────────────────────────────────────────────────────────────────────── -->
    <div class="space-y-5 lg:border-r lg:border-gray-100 lg:pr-6">

      <!-- Presets -->
      <div>
        <div class="text-xs uppercase tracking-wide text-gray-400 mb-2">Preset</div>
        <div class="grid grid-cols-3 gap-1.5">
          <button *ngFor="let p of presets" (click)="choosePreset(p)"
                  [ngClass]="theme.preset === p
                    ? 'bg-orange-50 border-orange-400 text-orange-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'"
                  class="px-2 py-2 text-xs rounded-lg border transition-colors">
            {{ presetLabel[p] }}
          </button>
        </div>
        <p class="text-[11px] text-gray-500 mt-1.5">
          Presets set corners, line shape, points and the soft tail. The knobs below tune on top.
        </p>
      </div>

      <!-- Scope -->
      <div>
        <div class="text-xs uppercase tracking-wide text-gray-400 mb-2">Applies to</div>
        <select [(ngModel)]="scope" (ngModelChange)="onScopeChange()"
                class="w-full rounded-lg bg-white border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-orange-400">
          <option value="">All charts</option>
          <option *ngFor="let s of scopes" [value]="s">Only {{ chartLabels[s] }}</option>
        </select>
        <p *ngIf="scope" class="text-[11px] text-gray-500 mt-1.5">
          Showing only the knobs that can differ per chart type.
        </p>
      </div>

      <!-- Knobs -->
      <div *ngFor="let k of visibleKnobs()">
        <div class="flex items-baseline justify-between mb-1.5">
          <span class="text-xs uppercase tracking-wide text-gray-400">{{ k.label }}</span>
          <!-- INHERITED vs SET. Without this the sparse-override model is invisible: there would be
               no way to tell which knobs still follow the layer above. -->
          <button *ngIf="isSet(k)" (click)="clear(k)"
                  class="text-[10px] text-orange-600 hover:text-orange-700">set · reset</button>
          <span *ngIf="!isSet(k)" class="text-[10px] text-gray-400">inherited</span>
        </div>
        <!-- A CONTINUOUS knob gets a slider. It interpolates between the same three vetted anchors
             the segmented control used to offer, so it unlocks nothing new — 0 and 100 are exactly
             the old Thin and Bold. -->
        <div *ngIf="k.slider; else segmented">
          <div class="flex items-center gap-2">
            <input type="range" class="flex-1 accent-orange-600"
                   [min]="k.slider.min" [max]="k.slider.max" [step]="k.slider.step"
                   [value]="current(k)"
                   (input)="setSlider(k, $any($event.target).value)" />
            <span class="text-[11px] text-gray-600 w-14 text-right">{{ sliderLabel(k) }}</span>
          </div>
          <div class="flex justify-between mt-0.5">
            <span *ngFor="let m of k.slider.marks" class="text-[10px] text-gray-400">{{ m.label }}</span>
          </div>
        </div>

        <ng-template #segmented>
          <div class="flex flex-wrap gap-1.5">
            <button *ngFor="let o of k.options" (click)="set(k, o.value)"
                    [ngClass]="current(k) === o.value
                      ? 'bg-orange-50 border-orange-400 text-orange-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'"
                    class="px-2.5 py-1.5 text-xs rounded-lg border transition-colors">
              {{ o.label }}
            </button>
          </div>
        </ng-template>
        <p class="text-[11px] text-gray-500 mt-1">{{ k.hint }}</p>
      </div>
    </div>

    <!-- ── Preview ──────────────────────────────────────────────────────────────────────────── -->
    <div>
      <div class="text-xs uppercase tracking-wide text-gray-400 mb-2">
        Preview — sample data, drawn by the real renderer
      </div>
      <!-- TWO columns, not three. A donut needs width for its ring, its outward labels AND a side
           legend; at three-up none of them had enough and the ring came out the size of a coin. -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div *ngFor="let c of fixture; let i = index"
             class="bg-white rounded-xl border border-gray-200 p-3 flex flex-col">
          <div class="text-[11px] font-medium text-gray-700 mb-1">{{ c.title }}</div>
          <div class="text-[10px] text-gray-400 mb-2">{{ c.form }}</div>

          <!-- ⚠ THE SAME COMPONENT A REAL PAGE USES. This screen previewed charts by calling the
               renderer itself for months, which meant the preview could drift from production
               silently — and did: it could not show the additivity clamp at all, because it never
               passed a measure. Rendering through <prism-plot> makes drift impossible rather than
               merely discouraged. The caption and the render notes come from the component too;
               showNotes is on here because this is a tuning surface. -->
          <prism-plot [spec]="c.spec" [config]="configFor(i)" [order]="c.spec.meta?.order || ''"
                      [showNotes]="true" width="100%" height="280px"></prism-plot>
        </div>
      </div>
    </div>
  </div>
</div>
